import * as React from "react";

import { cn } from "@/lib/utils";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
  /**
   * Accesibilidad (WCAG 2.2 1.3.1 + 3.3.2): todo <Input> visible debe llevar
   * un <label htmlFor={id}> asociado — el placeholder NO es label.
   * Patrón:
   *   <label htmlFor="email">Correo</label>
   *   <Input id="email" name="email" ... />
   * Para errores, combina con `describedBy` + `role="alert"` (ver abajo).
   * No rompe la API: solo documenta el requisito existente.
   */
  /**
   * Optional id of the hint/error element describing this input — rendered
   * as `aria-describedby` (WCAG 2.2 3.3.1 Error Identification, research
   * 2026-09-18). Error pattern:
   *   <Input error describedBy="email-error" ... />
   *   {error && <p id="email-error" role="alert">Revisa el correo.</p>}
   * (Direct `aria-describedby` via props still wins — spread stays last.)
   */
  describedBy?: string;
  /**
   * "hero": variante marketing opt-in — rounded 20px + foco hero-focus, superficie
   * opaca (2026-09-20 FASE 3, research R2 2026: sin blur sobre campo opaco).
   * El anillo hero pisa el `box-shadow:none` global de inputs por mayor
   * especificidad de clase.
   * 2026-09-21 FASE 3 FIXER (anillo 3:1: hero-focus-ring sólido en variant hero;
   * hero-focus al 45% queda solo en glows decorativos).
   * 2026-09-23 FASE 2 F-07 (research 2026 WCAG 2.2 AA 2.4.7/2.4.11 focus-visible:
   * el default lleva anillo 3px accent 40% + borde accent 45% — indicador ≥3px
   * y ≥3:1, sin depender solo del cambio de borde).
   */
  variant?: "default" | "hero";
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, error, describedBy, variant = "default", ...props },
    ref,
  ) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-11 w-full rounded-[var(--radius-field,16px)] border border-[var(--border)] bg-[var(--background-elevated)] px-3.5 py-1 max-sm:text-base sm:text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition-[border-color,box-shadow,background-color] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)] file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[color-mix(in_srgb,var(--accent)_45%,var(--border))] focus-visible:shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_65%,transparent)] disabled:cursor-not-allowed disabled:opacity-50",
          variant === "hero" &&
            "rounded-[var(--radius-hero-bento,20px)] bg-[var(--background-elevated)] focus-visible:border-[var(--hero-focus-ring,#a855f7)] focus-visible:shadow-[0_0_0_3px_var(--hero-focus-ring,#a855f7)]",
          error &&
            "border-[var(--danger)] focus-visible:ring-2 focus-visible:ring-[var(--danger)]",
          className,
        )}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
